/* eslint-disable @next/next/no-html-link-for-pages */
import Link from 'next/link';

import { GetPathName } from '@/lib/helpers';
import DownloadButton from '@/components/DownloadButton';
import {
  BasicHTMLPageCode,
  CookieConsentCode,
  DefaultJSExampleCode,
  FaviconsCode,
  HeadStylesheetCode,
} from '@/components/codeHighlights/ExampleCode';

const Home = () => {
  return (
    <>
      <h1 className="mt-0">Styleguide Web FHNW</h1>
      <p>
        Tags: <span className="badge bg-secondary">1.0.0</span>
        <span className="badge bg-info">Bootstrap v5.3</span>
        <span className="badge bg-warning">Stable</span>
        <span className="badge bg-success">Production</span>
      </p>

      <p className="lead">
        Der vorliegende Styleguide Web FHNW Version 1.0.0 leitet sich aus dem
        CD-Manual der FHNW ab und basiert auf dem CSS-Framework von{' '}
        <Link
          href="https://getbootstrap.com/docs/5.3/getting-started/introduction/"
          target="_blank"
        >
          Bootstrap
        </Link>
        .
      </p>

      <h2>Corporate Design</h2>
      <p>
        Der Styleguide Web FHNW definiert Erscheinungsbild und Gestaltung von
        Websites (insbesondere Webdiensten) und -Plattformen der FHNW gemäss
        CD-Manual. Der Styleguide wird technisch durch das Team Web der
        Corporate betreut (Kontaktangaben siehe Footer). Bei Fragen zum
        Corporate Design wenden Sie sich bitte an die{' '}
        <a href="mailto:kommunikation@fhnw.ch">Kommunikation FHNW</a>.
      </p>

      <h2 className="mt-5">Nutzung dieses Styleguides</h2>
      <p>
        Nachfolgend wird die technische Integration des
        FHNW-Bootstrap-Styleguides erläutert: insbesondere, welche Schritte
        erforderlich sind und welche Dateien zwingend integriert sein müssen, um
        HTML-Inhalte konform zum Corporate Design der FHNW darstellen zu können.
      </p>
      <p>
        Die Nutzung setzt Kenntnisse mit dem Bootstrap-Framework voraus,
        Know-How mit CSS erleichtert den Umgang mit dem Framework. Weitere
        Dokumentation ist auf der offiziellen Seite getbootstrap.com auffindbar.
      </p>
      <p>
        Technische Fragen bezogen auf die Implementation des vorliegenden
        Bootstrap-Frameworks können an{' '}
        <a href="mailto:webmaster@fhnw.ch">webmaster@fhnw.ch</a> gerichtet
        werden. Der Styleguide Web FHNW wird durch das Team Web der Corporate IT
        der FHNW bereitgestellt. Der Umgang und die Handhabung setzt Kenntnisse
        mit dem Bootstrap-Framework voraus. Kenntnisse in CSS erleichtert den
        Umgang mit dem Framework. Weitere und ausführliche Anleitungshilfen sind
        auf der offiziellen Seite{' '}
        <Link href="https://getbootstrap.com/" legacyBehavior>
          <a target="_blank" title="Getbootstrap">
            Getbootstrap
          </a>
        </Link>{' '}
        auffindbar.
      </p>

      <h2>Stylesheets</h2>
      <p>
        Im Starterpacket sind unter anderem zwei CSS-Dateien enthalten.{' '}
        <code>fhnw.css</code> und <code>fhnw.min.css</code>. Eine unkomprimierte
        und eine komprimierte CSS-Datei für den produktiven Einsatz. Es werden
        keine weiteren Stylesheets für die Darstelllung benötigt. Auch sind
        sämtliche erforderlichen Schriftschnitte der{' '}
        <code>Google Font Inter</code> im Stylesheet integriert. Es ist
        demzufolgend ausreichend die CSS-Datei wie unten im Code-Beispiel zu
        integrieren um die Darstellung gemäss FHNW CI/CD abbilden zu können.
      </p>
      <p>
        Die unkomprimierte CSS-Datei <code>fhnw.css</code> dient für Entwickler
        als unterstützendes Element um gewisse Einblicke in die Komponenten
        gewinnen zu können oder um z.B. weitere Plugins die CSS enthalten in
        einer komprimierten gebündelten CSS-Datei ausliefern zu können.
        Ansonsten ist die komprimierte Datei <code>fhnw.min.css</code> zu
        integrieren.
      </p>
      <div className="my-5">
        <HeadStylesheetCode />
      </div>

      <h2>Javascript</h2>
      <h3>default.js</h3>
      <p>
        Die Datei <code>default.js</code> enthält Beispiel-Skripte für diverse
        Komponenten. Diese Datei soll und kann nach belieben angepasst und
        überschrieben werden. Es enthält Javascript für die{' '}
        <Link href={'/basic-elements/form-validierung'} legacyBehavior>
          <a title="Form-Validierung">Form-Valierung</a>
        </Link>
        , initialer Code für den{' '}
        <a
          href="/basic-elements/webdienst-header"
          title="PAAS - Webdienst-Header"
        >
          Webdienst-Header
        </a>{' '}
        oder das jQuery Plugin <code>Select2</code> das für Form Select zur
        Anwendung kommt. Weitere Javascript-Beispiele zu den jeweiligen
        Komponenten sind aus der offziellen{' '}
        <Link
          href={
            'https://getbootstrap.com/docs/5.3/getting-started/introduction/'
          }
          legacyBehavior
        >
          <a title="Bootstrap-Dokumentation" target="_blank">
            Bootstrap-Dokumentation
          </a>
        </Link>{' '}
        zu entnehmen.
      </p>

      <div className="d-none d-md-block mt-5">
        <DefaultJSExampleCode />
      </div>

      <h3 className="mt-5">Cookie-Hinweis</h3>
      <p>
        In dieser Datei wird der Cookie-Hinweis gesteuert. Für Webseiten die als
        FHNW-Subdomain fungieren, kann der Code so belassen werden wie er ist.
        Für alle weiteren Domainen ist die <code>Zeile 5</code> entsprechend
        anzupassen. Weitere Details zur Funktionalität und zum Datenschutz sind
        auf der Seite{' '}
        <Link href={'/basic-elements/cookie-hinweis'} legacyBehavior>
          <a title="Cookie-Hinweis">Cookie-Hinweis</a>
        </Link>{' '}
        dokumentiert. Das Cookie-Consent-Skript ist im Ordner{' '}
        <code>/assets/js/cookieconsent.js</code> hinterlegt.
      </p>

      <div
        className="alert alert-secondary bg-black"
        role="alert"
        aria-label="Datenschutz und Cookies"
        aria-hidden="false"
      >
        <div className="row">
          <div className="col-10">
            <p className="text-white">
              Indem Sie diesen Hinweis schliessen oder mit dem Besuch der Seite
              fortfahren, akzeptieren Sie die Verwendung von Cookies. Weitere
              Informationen dazu finden Sie unter{' '}
              <a
                className="text-white"
                href="https://www.fhnw.ch/de/impressum/datenschutz"
                target="_blank"
              >
                Datenschutz
              </a>
              .
            </p>
          </div>
          <div className="col-2 mt-3">
            <button
              type="button"
              className="btn btn-secondary btn-sm acceptcookies float-right pl-3 pr-3"
              aria-label="Schliessen"
            >
              Ok
            </button>
          </div>
        </div>
      </div>

      <div className="d-none d-md-block mt-5">
        <CookieConsentCode />
      </div>

      <h3 className="mt-5">Favicons</h3>
      <p>
        In der Beispiel-HTML sind im <code>head</code> Favicons bereits
        eingepflegt. Die Favicons sind im Ordner <code>/assets/img/device</code>{' '}
        hinterlegt.
      </p>
      <div className="d-none d-md-block mt-5">
        <FaviconsCode />
      </div>

      <h2 className="mt-5">Beispiel-HTML mit allen benötigten Ressourcen</h2>
      <div className="d-none d-md-block mt-5">
        <BasicHTMLPageCode />
      </div>

      <h2 className="mt-5">Download Starterpacket</h2>
      <p>Alle benötigten Ressourcen sind im Starterpacket enthalten.</p>
      <div className="col-12 col-md-6 my-5">
        <DownloadButton styles="btn-secondary w-100" />
      </div>
    </>
  );
};

export default Home;
